/**
 * Bulk Application Progress Tracker Models
 */

// ============================================================================
// ENUMS
// ============================================================================

export enum ProgressState {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  PARTIAL_FAILURE = 'PARTIAL_FAILURE',
  COMPLETE_FAILURE = 'COMPLETE_FAILURE',
}

export enum ValidationStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

// ============================================================================
// API RESPONSE INTERFACES
// ============================================================================

export interface BulkProgressResponse {
  applicationNumber: string;
  billId: string | null;
  totalExpectedChildApplications: number;
  totalFailedChildApplications: number;
  totalSuccessfulChildApplications: number;
  status: ProgressState;
  percentageComplete: number;
  price: number;
  lastUpdated: string; // ISO 8601 datetime
}

export interface ChildApplicationDto {
  childApplicationNumber: string;
  displayIdentifier: string;
  validationStatus: ValidationStatus;
  errorMessage: string | null;
  errorCode: string | null;
  errorMetadata: Record<string, unknown> | null;
  validatedAt: string; // ISO 8601 datetime
}

export interface ChildApplicationsResponse {
  masterApplicationNumber: string;
  validationStatus: ValidationStatus;
  applications: ChildApplicationDto[];
  totalCount: number;
}

// ============================================================================
// COMPONENT INTERFACES
// ============================================================================

export interface ProgressLabels {
  // Progress section
  processingMessage?: string;
  invalidLabel?: string;
  validLabel?: string;
  itemsChecked?: string;

  // Failed items section
  downloadReport?: string;

  // Table headers
  identifierHeader?: string;
  errorMessageHeader?: string;
}

export interface CompletionEvent {
  applicationNumber: string;
  billId: string;
  totalSuccessful: number;
  totalFailed: number;
}

export interface FailureEvent {
  applicationNumber: string;
  totalFailed: number;
}

export interface BillReadyEvent {
  applicationNumber: string;
  billId: string;
}

export interface ProgressError {
  message: string;
  error: any;
}

// ============================================================================
// DEFAULT LABELS
// ============================================================================
export const DEFAULT_PROGRESS_LABELS: Required<ProgressLabels> = {
  processingMessage: 'BULK_PROGRESS.PROCESSING_MESSAGE',
  invalidLabel: 'BULK_PROGRESS.INVALID_LABEL',
  validLabel: 'BULK_PROGRESS.VALID_LABEL',
  itemsChecked: 'BULK_PROGRESS.ITEMS_CHECKED',
  downloadReport: 'BULK_PROGRESS.DOWNLOAD_REPORT',
  identifierHeader: 'BULK_PROGRESS.IDENTIFIER_HEADER',
  errorMessageHeader: 'BULK_PROGRESS.ERROR_MESSAGE_HEADER',
};
